// ============================================================================
// CardioEditor — the add-a-cardio sheet. Optional activity (via ExercisePicker)
// plus distance / time / avg+max HR / intensity / calories override / notes.
// Wires to useAddCardio. Optional-first: every field can be blank — a cardio
// entry can be just "20 min easy". Mobile-first, sticky add bar.
// ============================================================================
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, SelectField, TextField } from "../../components";
import { useAddCardio } from "../../hooks/sessions";
import type { CardioInput, Exercise } from "../../api";
import { ExercisePicker } from "./ExercisePicker";
import { Sheet } from "./Sheet";
import { exerciseName, parseInt0, parseNum } from "./lib";
import { cn } from "../../lib/cn";

interface CardioEditorProps {
  open: boolean;
  onClose: () => void;
  sessionId: number;
}

export function CardioEditor({ open, onClose, sessionId }: CardioEditorProps) {
  const { t, i18n } = useTranslation("sessions");
  const addCardio = useAddCardio();

  const [picker, setPicker] = useState(false);
  const [exercise, setExercise] = useState<Exercise | null>(null);
  const [distance, setDistance] = useState("");
  const [time, setTime] = useState("");
  const [avgHr, setAvgHr] = useState("");
  const [maxHr, setMaxHr] = useState("");
  const [intensity, setIntensity] = useState("");
  const [calories, setCalories] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (!open) return;
    setError(undefined);
  }, [open]);

  const submit = () => {
    setError(undefined);
    const body: CardioInput = {
      exercise_id: exercise?.id ?? null,
      distance: parseNum(distance),
      time_min: parseNum(time),
      avg_hr: parseInt0(avgHr),
      max_hr: parseInt0(maxHr),
      intensity: intensity || null,
      calories_override: parseNum(calories),
      notes: notes.trim() || null,
    };
    addCardio.mutate(
      { sessionId, body },
      {
        onSuccess: () => {
          setExercise(null);
          setDistance("");
          setTime("");
          setAvgHr("");
          setMaxHr("");
          setIntensity("");
          setCalories("");
          setNotes("");
          onClose();
        },
        onError: () => setError(t("cardio.saveError")),
      },
    );
  };

  return (
    <>
      <Sheet
        open={open}
        onClose={onClose}
        title={t("cardio.add")}
        footer={
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={onClose} disabled={addCardio.isPending}>
              {t("form.cancel")}
            </Button>
            <div className="flex-1" />
            <Button onClick={submit} disabled={addCardio.isPending}>
              {addCardio.isPending ? t("cardio.saving") : t("cardio.save")}
            </Button>
          </div>
        }
      >
        <div className="space-y-4 py-1">
          {/* Activity picker trigger */}
          <button
            type="button"
            onClick={() => setPicker(true)}
            className="flex w-full items-center gap-3 rounded-md bg-surface p-3 text-left ring-1 ring-line-strong shadow-xs transition-shadow hover:ring-volt focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt"
          >
            <span
              className={cn(
                "grid h-11 w-11 shrink-0 place-items-center rounded-md text-white",
                exercise ? "bg-grad-coral" : "bg-grad-lime",
              )}
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
                <path d="M13 4.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3ZM7 21l3-6 3 2 1 4M10 15l-1-4 4-2 3 3h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <span className="min-w-0 flex-1">
              <span className="block font-display text-xs font-bold uppercase tracking-kicker text-body-soft">
                {t("cardio.exercise")}
              </span>
              <span className="block truncate font-display text-base font-bold text-body">
                {exercise
                  ? exerciseName(exercise, i18n.language) || `#${exercise.id}`
                  : t("cardio.pickExercise")}
              </span>
            </span>
            <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0 text-body-faint" fill="none" aria-hidden>
              <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          {/* Distance + time */}
          <div className="grid grid-cols-2 gap-3">
            <TextField
              label={t("cardio.distance")}
              type="number"
              inputMode="decimal"
              min={0}
              value={distance}
              onChange={(e) => setDistance(e.target.value)}
              suffix={t("cardio.distanceUnit")}
            />
            <TextField
              label={t("cardio.time")}
              type="number"
              inputMode="decimal"
              min={0}
              value={time}
              onChange={(e) => setTime(e.target.value)}
              suffix={t("cardio.timeUnit")}
            />
          </div>

          {/* HR */}
          <div className="grid grid-cols-2 gap-3">
            <TextField
              label={t("cardio.avgHr")}
              type="number"
              inputMode="numeric"
              min={0}
              value={avgHr}
              onChange={(e) => setAvgHr(e.target.value)}
              suffix={t("cardio.bpm")}
            />
            <TextField
              label={t("cardio.maxHr")}
              type="number"
              inputMode="numeric"
              min={0}
              value={maxHr}
              onChange={(e) => setMaxHr(e.target.value)}
              suffix={t("cardio.bpm")}
            />
          </div>

          {/* Intensity + calories */}
          <div className="grid grid-cols-2 gap-3">
            <SelectField
              label={t("cardio.intensity")}
              placeholder="—"
              value={intensity}
              onChange={(e) => setIntensity(e.target.value)}
              options={[
                { label: t("cardio.intensityEasy"), value: "easy" },
                { label: t("cardio.intensityModerate"), value: "moderate" },
                { label: t("cardio.intensityHard"), value: "hard" },
              ]}
            />
            <TextField
              label={t("cardio.caloriesOverride")}
              type="number"
              inputMode="numeric"
              min={0}
              value={calories}
              onChange={(e) => setCalories(e.target.value)}
              suffix={t("log.calUnit")}
            />
          </div>

          {/* Notes */}
          <TextField
            label={t("cardio.notes")}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />

          {error && (
            <p className="text-sm font-medium text-danger" role="alert">
              {error}
            </p>
          )}
        </div>
      </Sheet>

      <ExercisePicker
        open={picker}
        onClose={() => setPicker(false)}
        onPick={setExercise}
      />
    </>
  );
}
